import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from './api'

interface StoredProgress {
  lastIndex: number
  updatedAt: string
}

const SERVER_SYNC_DEBOUNCE_MS = 700

function storageKey(moduleId: string): string {
  return `module-progress:${moduleId}`
}

function readStored(moduleId: string): StoredProgress | null {
  try {
    const raw = localStorage.getItem(storageKey(moduleId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredProgress
    if (typeof parsed.lastIndex !== 'number' || parsed.lastIndex < 0) return null
    return parsed
  } catch {
    return null
  }
}

function writeStored(moduleId: string, lastIndex: number): void {
  try {
    const payload: StoredProgress = {
      lastIndex,
      updatedAt: new Date().toISOString(),
    }
    localStorage.setItem(storageKey(moduleId), JSON.stringify(payload))
  } catch {
    // Safari private mode + quota errors — ignore
  }
}

function clamp(n: number, lo: number, hi: number): number {
  if (hi < lo) return lo
  return Math.max(lo, Math.min(hi, n))
}

export interface DeckProgress {
  initialIndex: number
  persist: (index: number) => void
}

export function useDeckProgress(
  moduleId: string | undefined,
  total: number,
  serverIndex?: number,
): DeckProgress {
  const [initialIndex] = useState(() => {
    if (!moduleId || total <= 0) return 0
    const stored = readStored(moduleId)
    const local = stored?.lastIndex ?? 0
    const server = serverIndex ?? 0
    return clamp(Math.max(local, server), 0, total - 1)
  })

  const moduleIdRef = useRef(moduleId)
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    moduleIdRef.current = moduleId
  }, [moduleId])

  // Cancel pending sync on unmount.
  useEffect(() => {
    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current)
    }
  }, [])

  const persist = useCallback((index: number) => {
    const id = moduleIdRef.current
    if (!id) return

    writeStored(id, index)

    // Debounced server sync — flushes 700ms after the last slide change.
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current)
    syncTimerRef.current = setTimeout(() => {
      api
        .updateProgress({
          module_id: id,
          status: 'in_progress',
          last_slide_index: index,
        })
        .catch(() => {
          // Network errors are non-fatal — local state remains the source of
          // truth until the next successful sync.
        })
    }, SERVER_SYNC_DEBOUNCE_MS)
  }, [])

  return { initialIndex, persist }
}
